import { PDFDocument } from 'pdf-lib'
// @ts-ignore
import fontkit from '@pdf-lib/fontkit'
// @ts-ignore
import Chart from 'chart.js'
import { PdfReportData, ImagesRapport, PolicesRapport, Mesure, documentRapport, dimensionsPage10, HAUTEUR_FLUX, HAUTEUR_JOUR_TYPE, journeeType, libelleSurplus as libelleSurplusRapport } from './rapportHtml'

// ── Couleurs ──────────────────────────────────────────────────────────────
var C_YEL = '#f5c518'
var C_ORA = '#ff9800'
var C_GRN = '#4caf50'
var C_BLU = '#1976d2'
var C_RED = '#e53935'
var C_GRY = '#90a4ae'
var MONTHS_SHORT = ['Jan','Fév','Mar','Avr','Mai','Juin','Juil','Août','Sep','Oct','Nov','Déc']

/** Graduations à la française (« 25 000 € », « 1,5 ») — 16/10/2026 : « 25000 » sans séparateur. */
function graduation(suffixe: string, decimales?: number) {
  return function (v: number) {
    var t = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: decimales || 0 }).format(v).replace(/[\u202f\u00a0]/g, ' ')
    return t + suffixe
  }
}
function axeY(o?: { suffixe?: string; decimales?: number; zero?: boolean; extra?: any }): any {
  o = o || {}
  var a: any = { gridLines: { color: '#f0f0f0' }, ticks: { fontColor: '#444', fontSize: 13, callback: graduation(o.suffixe || '', o.decimales) }, scaleLabel: { display: false } }
  if (o.zero) a.ticks.beginAtZero = true
  if (o.extra) Object.keys(o.extra).forEach(function (k) { a[k] = Object.assign({}, a[k] || {}, o!.extra[k]) })
  return a
}

function rgba(hex: string, a: number): string {
  var r = parseInt(hex.slice(1,3), 16)
  var g = parseInt(hex.slice(3,5), 16)
  var b = parseInt(hex.slice(5,7), 16)
  return 'rgba(' + r + ',' + g + ',' + b + ',' + a + ')'
}

// ── Rendu Chart.js hors écran ─────────────────────────────────────────────
function renderChart(cfg: any, w: number, h: number, dpr = 1): string {
  var el = document.createElement('canvas')
  el.width = w * dpr
  el.height = h * dpr
  el.style.cssText = 'position:fixed;left:-99999px;top:-99999px;pointer-events:none'
  document.body.appendChild(el)
  if (!cfg.options) cfg.options = {}
  cfg.options.animation = { duration: 0 }
  cfg.options.responsive = false
  cfg.options.maintainAspectRatio = false
  if (dpr !== 1) cfg.options.devicePixelRatio = dpr
  var chart = new Chart(el, cfg)
  var url = el.toDataURL('image/png')
  chart.destroy()
  document.body.removeChild(el)
  return url
}

// ── Rendu Sankey Canvas2D ─────────────────────────────────────────────────
// Canvas dimensionné à 2× le champ PDF (539×364 pts) → 1078×728 px
function renderSankey(d: PdfReportData, w: number, h: number): string {
  var el = document.createElement('canvas')
  el.width = w
  el.height = h
  document.body.appendChild(el)
  var ctx = el.getContext('2d')!
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, w, h)

  var hasBat = d.hasBattery && d.batteryKwh > 0
  var totalProd = d.autoconsoDir + (hasBat ? d.autoconsoBat + d.pertesBat : 0) + d.surplus
  var totalConso = d.autoconsoDir + (hasBat ? d.autoconsoBat : 0) + d.reseau

  // Scale = totalProd + reseau = totalConso + surplus + pertesBat (bilan énergétique)
  // Garantit que tous les nœuds gauche ET droite tiennent dans aH sans débordement
  var energyTotal = Math.max(totalProd + d.reseau, 1)

  var nodeW = 14
  var pL = 210, pR = 300, pT = 30, pB = 30
  var aH = h - pT - pB
  var gap = 14

  // Nombre de gaps par colonne
  var nGapL = d.reseau > 0 ? 1 : 0
  var nGapR = (d.surplus > 0 ? 1 : 0) + (hasBat && d.pertesBat > 0 ? 1 : 0)
  var aHNodes = aH - Math.max(nGapL, nGapR) * gap

  var hSolar   = (totalProd / energyTotal) * aHNodes
  var hReseau  = (d.reseau   / energyTotal) * aHNodes
  var hDir     = (d.autoconsoDir / energyTotal) * aHNodes
  var hBatFlow = hasBat ? ((d.autoconsoBat + d.pertesBat) / energyTotal) * aHNodes : 0
  var hAutcBat = hasBat ? (d.autoconsoBat / energyTotal) * aHNodes : 0
  var hPertes  = hasBat ? (d.pertesBat    / energyTotal) * aHNodes : 0
  var hSurplus = (d.surplus  / energyTotal) * aHNodes
  var hConso   = (totalConso / energyTotal) * aHNodes

  var xSrc = pL
  var xMid = w / 2 - nodeW / 2
  var xDst = w - pR - nodeW

  // Positions verticales — tout ancré dans [pT, pT + aH]
  var ySolar   = pT
  var yReseau  = pT + hSolar + (nGapL > 0 ? gap : 0)
  var yBat     = pT + hDir + gap / 2
  var yConso   = pT
  var ySurplus = pT + hConso + (d.surplus > 0 ? gap : 0)
  var yPertes  = ySurplus + hSurplus + (hasBat && d.pertesBat > 0 ? gap : 0)

  function flow(x1: number, y1t: number, h1: number, x2: number, y2t: number, h2: number, color: string) {
    if (h1 < 2 || h2 < 2) return
    var rr = parseInt(color.slice(1,3),16), gg = parseInt(color.slice(3,5),16), bb = parseInt(color.slice(5,7),16)
    var cp = (x1 + nodeW + x2) / 2
    var g = ctx.createLinearGradient(x1 + nodeW, 0, x2, 0)
    g.addColorStop(0, 'rgba('+rr+','+gg+','+bb+',0.6)')
    g.addColorStop(1, 'rgba('+rr+','+gg+','+bb+',0.2)')
    ctx.fillStyle = g
    ctx.beginPath()
    ctx.moveTo(x1+nodeW, y1t)
    ctx.bezierCurveTo(cp, y1t, cp, y2t, x2, y2t)
    ctx.lineTo(x2, y2t+h2)
    ctx.bezierCurveTo(cp, y2t+h2, cp, y1t+h1, x1+nodeW, y1t+h1)
    ctx.closePath()
    ctx.fill()
  }

  // étiquettes de la colonne de droite : petits flux voisins (surplus, pertes) → étiquettes décalées pour ne pas se chevaucher
  var basDerniereEtiquetteDroite = -Infinity
  function node(x: number, yt: number, hn: number, color: string, label: string, valKwh: number, left: boolean) {
    if (hn < 2) return
    ctx.fillStyle = color
    ctx.fillRect(x, yt, nodeW, hn)
    var lx = left ? x - 8 : x + nodeW + 8
    var ly = yt + hn / 2
    if (x === xDst) {
      ly = Math.max(ly, basDerniereEtiquetteDroite + 56)
      basDerniereEtiquetteDroite = ly
    }
    ctx.textAlign = left ? 'right' : 'left'
    ctx.font = 'bold 26px Arial'
    ctx.fillStyle = '#333'
    ctx.fillText(label, lx, ly - 4)
    ctx.font = '22px Arial'
    ctx.fillStyle = '#555'
    ctx.fillText(Math.round(valKwh).toLocaleString('fr-FR').replace(/[\u202f\u00a0]/g, ' ') + ' kWh', lx, ly + 22)
  }

  var ySC = ySolar, yCC = yConso
  if (hDir > 2)    { flow(xSrc, ySC, hDir, xDst, yCC, hDir, C_YEL); ySC += hDir; yCC += hDir }
  if (hasBat && hBatFlow > 2) {
    flow(xSrc, ySC, hBatFlow, xMid, yBat, hBatFlow, C_ORA); ySC += hBatFlow
    if (hAutcBat > 2) { flow(xMid, yBat, hAutcBat, xDst, yCC, hAutcBat, C_ORA); yCC += hAutcBat }
    if (hPertes  > 2)   flow(xMid, yBat+hAutcBat, hPertes, xDst, yPertes, hPertes, C_RED)
  }
  if (hSurplus > 2) flow(xSrc, ySC,      hSurplus, xDst, ySurplus, hSurplus, C_GRN)
  if (hReseau  > 2) flow(xSrc, yReseau,  hReseau,  xDst, yCC,      hReseau,  C_BLU)

  node(xSrc, ySolar,   hSolar,   C_YEL,    'Solaire',         totalProd,                      true)
  if (hReseau  > 2) node(xSrc, yReseau,  hReseau,  C_BLU,    'Réseau',          d.reseau,     true)
  if (hasBat && hBatFlow > 2) node(xMid, yBat, hBatFlow, C_ORA, 'Batterie', d.autoconsoBat + d.pertesBat, false)
  if (hConso   > 2) node(xDst, yConso,   hConso,   '#546e7a', 'Consommation',    totalConso,   false)
  if (hSurplus > 2) node(xDst, ySurplus, hSurplus, C_GRN,    libelleSurplus(d), d.surplus,    false)
  if (hasBat && hPertes > 2) node(xDst, yPertes, hPertes, C_RED, 'Pertes batterie', d.pertesBat,  false)

  var url = el.toDataURL('image/png')
  document.body.removeChild(el)
  return url
}

// ── Configs Chart.js ──────────────────────────────────────────────────────
/**
 * Journée type de la page 9. Puissances (kW) sur l'axe de gauche, même échelle pour les 8 journées (maxKw) ; stock de la
 * batterie (kWh) sur l'axe de droite, de 0 à la capacité — 16/10/2026 : le stock écrasait les autres courbes.
 */
function cfgJourType(d: PdfReportData, m: any, maxKw: number): any {
  m = m || {}
  var labels: string[] = []
  for (var h = 0; h < 24; h++) labels.push(h + 'h')
  var arr = function (a: any) { return (a || []).map(function (v: number) { return +(v || 0).toFixed(2) }) }
  var ds: any[] = [
    { label: 'Production', data: arr(m.firstDayHourlyProduction), borderColor: C_YEL, backgroundColor: rgba(C_YEL,0.15), borderWidth:3, pointRadius:0, fill:true, yAxisID: 'kw' },
    { label: 'Consommation', data: arr(m.firstDayHourlyConsumption), borderColor: C_BLU, backgroundColor:'transparent', borderWidth:3, pointRadius:0, fill:false, yAxisID: 'kw' },
    { label: 'Soutirage réseau', data: arr(m.firstDayHourlyPurchase), borderColor: C_RED, backgroundColor:rgba(C_RED,0.08), borderWidth:2.5, pointRadius:0, fill:true, yAxisID: 'kw' },
  ]
  var axes: any[] = [Object.assign(axeY({ suffixe: ' kW', decimales: 1, extra: { ticks: { min: 0, max: maxKw, maxTicksLimit: 6, fontSize: 18 } } }), { id: 'kw', position: 'left' })]
  if (d.hasBattery) {
    // stock en % de la capacité de la batterie, axe fixe de 0 à 100 % (retour du 16/10/2026)
    var capacite = d.batteryKwh > 0 ? d.batteryKwh : 1
    var stockPct = (m.firstDayHourlyBatteryStock || []).map(function (v: number) { return +Math.min(100, (v || 0) / capacite * 100).toFixed(1) })
    ds.push({ label: 'Stock batterie', data: stockPct, borderColor: C_ORA, backgroundColor:'transparent', borderWidth:2.5, borderDash:[8, 5], pointRadius:0, fill:false, yAxisID: 'pct' })
    axes.push(Object.assign(axeY({ suffixe: ' %', extra: { gridLines: { display: false }, ticks: { min: 0, max: 100, stepSize: 25, fontSize: 18, fontColor: C_ORA } } }), { id: 'pct', position: 'right' }))
  }
  return { type: 'line', data: { labels: labels, datasets: ds }, options: { legend: { display: false }, scales: {
    xAxes: [{ gridLines:{display:false}, ticks:{fontColor:'#444',fontSize:18,maxRotation:0,autoSkip:true,maxTicksLimit:9} }], yAxes: axes } } }
}
/** Puissance maximale (kW, arrondie au-dessus) des journées types affichées : une seule échelle pour la page 9. */
function maxJoursTypes(d: PdfReportData, jours: any[]): number {
  var max = 0
  jours.forEach(function (m) {
    m = m || {}
    ;[m.firstDayHourlyProduction, m.firstDayHourlyConsumption, m.firstDayHourlyPurchase].forEach(function (a: any) {
      (a || []).forEach(function (v: number) { if (v > max) max = v })
    })
  })
  if (max <= 0) return 1
  var pas = max <= 2 ? 0.5 : max <= 10 ? 1 : max <= 50 ? 5 : Math.pow(10, Math.floor(Math.log10(max)))
  return Math.ceil(max * 1.05 / pas) * pas
}

function cfgJourTypeSaisonnier(d: PdfReportData): any {
  // journées les plus représentatives de la consommation de l'hiver et de l'été (03/11/2026) ; calcul plus ancien :
  // premier mercredi et premier dimanche de février et d'août
  var conso = function (saison: string, we: boolean): any {
    var j = journeeType(d, saison, we, 'conso')
    return j ? j.consommation : null
  }
  var feb = d.monthlyData[1] || {}  // février
  var aug = d.monthlyData[7] || {}  // août
  feb = { firstWednesdayHourlyConsumption: conso('hiver', false) || feb.firstWednesdayHourlyConsumption,
    firstSundayHourlyConsumption: conso('hiver', true) || feb.firstSundayHourlyConsumption }
  aug = { firstWednesdayHourlyConsumption: conso('ete', false) || aug.firstWednesdayHourlyConsumption,
    firstSundayHourlyConsumption: conso('ete', true) || aug.firstSundayHourlyConsumption }
  var labels: string[] = []
  for (var h = 0; h < 24; h++) labels.push(h + 'h')
  var toArr = (a: any) => (a || []).map((v: number) => +(v || 0).toFixed(2))
  var C_BLU_D = '#1565c0'   // bleu foncé — hiver semaine
  var C_BLU_L = '#90caf9'   // bleu clair  — hiver weekend
  var C_ORA_D = '#e65100'   // orange foncé — été semaine
  var C_ORA_L = '#ffcc80'   // orange clair  — été weekend
  var ds = [
    { label: 'Hiver — Semaine (mercredi)',  data: toArr(feb.firstWednesdayHourlyConsumption), borderColor: C_BLU_D, backgroundColor: 'transparent', borderWidth: 2, pointRadius: 1, fill: false, borderDash: [] },
    { label: 'Hiver — Weekend (dimanche)',  data: toArr(feb.firstSundayHourlyConsumption),    borderColor: C_BLU_L, backgroundColor: 'transparent', borderWidth: 2, pointRadius: 1, fill: false, borderDash: [5, 3] },
    { label: 'Été — Semaine (mercredi)',    data: toArr(aug.firstWednesdayHourlyConsumption), borderColor: C_ORA_D, backgroundColor: 'transparent', borderWidth: 2, pointRadius: 1, fill: false, borderDash: [] },
    { label: 'Été — Weekend (dimanche)',    data: toArr(aug.firstSundayHourlyConsumption),    borderColor: C_ORA_L, backgroundColor: 'transparent', borderWidth: 2, pointRadius: 1, fill: false, borderDash: [5, 3] },
  ]
  return {
    type: 'line',
    data: { labels, datasets: ds },
    options: {
      legend: { display: false },
      scales: {
        xAxes: [{ gridLines: { display: false }, ticks: { fontColor: '#444', fontSize: 13 } }],
        yAxes: [axeY({ decimales: 1, zero: true, extra: { scaleLabel: { display: true, labelString: 'kW', fontColor: '#666', fontSize: 13 } } })]
      }
    }
  }
}

function cfgProdConso(d: PdfReportData): any {
  return { type: 'bar', data: { labels: MONTHS_SHORT, datasets: [
    { label: 'Production (kWh)', type:'bar', data: d.monthlyData.map((m: any) => Math.round(m.production||0)), backgroundColor:rgba(C_YEL,0.8), borderColor:C_YEL, borderWidth:1, yAxisID:'y' },
    { label: 'Consommation (kWh)', type:'line', data: d.monthlyData.map((m: any) => Math.round(m.consumption||0)), borderColor:C_BLU, backgroundColor:'transparent', borderWidth:2, pointRadius:3, fill:false, yAxisID:'y' },
  ]}, options: { legend:{display:false}, scales: { xAxes:[{gridLines:{display:false},ticks:{fontColor:'#444',fontSize:13}}], yAxes:[Object.assign(axeY({ zero: true }), { id: 'y' })] } } }
}

function cfgDonut(labels: string[], data: number[], colors: string[], showLegend: boolean): any {
  return { type: 'doughnut', data: { labels, datasets: [{ data, backgroundColor: colors, borderWidth:2, borderColor:'#fff' }] }, options: { cutoutPercentage:65, legend:{display:showLegend,position:'bottom',labels:{fontSize:13,usePointStyle:true,boxWidth:10}}, tooltips:{enabled:false} } }
}

function cfgDonutProd(d: PdfReportData): any {
  if (d.hasBattery && d.autoconsoBat > 0) return cfgDonut(['Autoconsommé direct','Via batterie',libelleSurplus(d),'Pertes batterie'], [Math.round(d.autoconsoDir),Math.round(d.autoconsoBat),Math.round(d.surplus),Math.round(d.pertesBat)], [C_YEL,C_ORA,C_GRN,C_RED], false)
  return cfgDonut(['Autoconsommé',libelleSurplus(d)], [Math.round(d.autoconsoDir),Math.round(d.surplus)], [C_YEL,C_GRN], false)
}

function cfgDonutConso(d: PdfReportData): any {
  if (d.hasBattery && d.autoconsoBat > 0) return cfgDonut(['Solaire direct','Via batterie','Réseau'], [Math.round(d.autoconsoDir),Math.round(d.autoconsoBat),Math.round(d.reseau)], [C_YEL,C_ORA,C_BLU], false)
  return cfgDonut(['Solaire direct','Réseau'], [Math.round(d.autoconsoDir),Math.round(d.reseau)], [C_YEL,C_BLU], false)
}

function cfgDonutEco(d: PdfReportData): any {
  if (d.hasBattery && d.ecoBat > 0) return cfgDonut(['Autoconso directe','Via batterie','Vente de surplus'], [Math.round(d.ecoAutoconsoDir*100)/100,Math.round(d.ecoBat*100)/100,Math.round(d.rachatSurplus*100)/100], [C_YEL,C_ORA,C_GRN], false)
  return cfgDonut(['Autoconso directe','Vente de surplus'], [Math.round(d.ecoAutoconsoDir*100)/100,Math.round(d.rachatSurplus*100)/100], [C_YEL,C_GRN], false)
}

function cfgRepartition(d: PdfReportData): any {
  var totalAC = Math.max(d.autoconsoDir + d.autoconsoBat, 1)
  var ds: any[] = [{ label: 'Autoconso. directe (kWh)', data: d.monthlyData.map((m: any) => Math.round((m.autoConsumption||0)*d.autoconsoDir/totalAC)), backgroundColor:rgba(C_YEL,0.85), borderColor:C_YEL, borderWidth:1 }]
  if (d.hasBattery && d.autoconsoBat > 0) ds.push({ label: 'Via batterie (kWh)', data: d.monthlyData.map((m: any) => Math.round((m.autoConsumption||0)*d.autoconsoBat/totalAC)), backgroundColor:rgba(C_ORA,0.85), borderColor:C_ORA, borderWidth:1 })
  ds.push({ label: libelleSurplus(d) + ' (kWh)', data: d.monthlyData.map((m: any) => Math.round(Math.max(0,(m.production||0)-(m.autoConsumption||0)))), backgroundColor:rgba(C_GRN,0.75), borderColor:C_GRN, borderWidth:1 })
  return { type:'bar', data:{labels:MONTHS_SHORT,datasets:ds}, options:{legend:{display:false}, scales:{xAxes:[{stacked:true,gridLines:{display:false},ticks:{fontColor:'#444',fontSize:13}}],yAxes:[Object.assign(axeY({ zero: true }), { stacked: true })]}} }
}

function cfgProjection(d: PdfReportData): any {
  var labels: string[] = []
  for (var yr = 1; yr <= (d.projectionBalance || []).length; yr++) labels.push('An ' + yr)
  var bal = d.projectionBalance

  // Trouver le point de rentabilité
  var roiInfo: any = null
  // même point de départ que l'écran (Step6Rapport.projectionRoiInfo) ; le raccordement est déjà dans coutNetTTC
  var prevBal = -(d.coutInvestissementTotal != null ? d.coutInvestissementTotal : d.coutNetTTC)
  for (var i = 0; i < bal.length; i++) {
    if (bal[i] >= 0 && prevBal < 0) {
      var frac = Math.abs(prevBal) / (Math.abs(prevBal) + Math.abs(bal[i]))
      roiInfo = { index: i, fraction: frac, years: (i + frac).toFixed(1).replace('.', ',') }
      break
    }
    prevBal = bal[i]
  }

  var plugins = roiInfo ? [{
    afterDraw: function(chart: any) {
      var ctx2 = chart.ctx
      var meta = chart.getDatasetMeta(0)
      if (!meta || !meta.data || !meta.data.length) return
      var idx = roiInfo.index
      var currBar = meta.data[idx]
      if (!currBar) return
      var prevBar = idx > 0 ? meta.data[idx - 1] : null
      var xCurr = currBar._model.x
      var xPrev = prevBar ? prevBar._model.x : xCurr - (currBar._model.width || 40)
      var x = xPrev + (xCurr - xPrev) * roiInfo.fraction
      var chartArea = chart.chartArea
      ctx2.save()
      ctx2.beginPath()
      ctx2.setLineDash([8, 5])
      ctx2.moveTo(x, chartArea.top)
      ctx2.lineTo(x, chartArea.bottom)
      ctx2.strokeStyle = '#e53935'
      ctx2.lineWidth = 3
      ctx2.stroke()
      ctx2.setLineDash([])
      ctx2.font = 'bold 18px Arial'
      ctx2.fillStyle = '#e53935'
      ctx2.textAlign = x > chart.width - 180 ? 'right' : 'left'
      var labelX = x > chart.width - 180 ? x - 10 : x + 10
      ctx2.fillText('Rentabilité : ' + roiInfo.years + ' ans', labelX, chartArea.top + 24)
      ctx2.restore()
    }
  }] : []

  return {
    type: 'bar',
    data: { labels, datasets: [{ label: 'Bénéfice net cumulé (€)', data: bal, backgroundColor: bal.map(function(v) { return v >= 0 ? rgba(C_GRN, 0.75) : rgba(C_RED, 0.65) }), borderColor: bal.map(function(v) { return v >= 0 ? C_GRN : C_RED }), borderWidth: 1 }] },
    plugins: plugins,
    options: { legend: { display: false }, scales: { xAxes: [{ gridLines: { display: false }, ticks: { fontColor: '#444', fontSize: 13 } }], yAxes: [axeY({ suffixe: ' €', extra: { gridLines: { zeroLineColor: '#999', zeroLineWidth: 2 } } })] } }
  }
}

function cfgFactures(d: PdfReportData): any {
  return { type:'bar', data:{labels:MONTHS_SHORT,datasets:[
    { label:'Facture Avant (€)', data:d.monthlyData.map((m: any)=>Math.round((m.currentBilling||0)*100)/100), backgroundColor:rgba(C_GRY,0.7), borderColor:C_GRY, borderWidth:1 },
    { label:'Facture Après (€)', data:d.monthlyData.map((m: any)=>Math.round((m.newBilling||0)*100)/100), backgroundColor:rgba(C_YEL,0.85), borderColor:C_YEL, borderWidth:1 },
  ]}, options:{legend:{display:false}, scales:{xAxes:[{gridLines:{display:false},ticks:{fontColor:'#444',fontSize:13}}],yAxes:[axeY({ suffixe: ' €', zero: true })]}} }
}

function cfgReseau(d: PdfReportData): any {
  return { type:'line', data:{labels:MONTHS_SHORT,datasets:[
    { label:'Soutirage Avant (kWh)', data:d.monthlyData.map((m: any)=>Math.round(m.consumption||0)), borderColor:C_GRY, backgroundColor:rgba(C_GRY,0.15), borderWidth:2, pointRadius:3, fill:true },
    { label:'Soutirage Après (kWh)', data:d.monthlyData.map((m: any)=>Math.round(m.purchase||0)), borderColor:C_YEL, backgroundColor:rgba(C_YEL,0.2), borderWidth:2, pointRadius:3, fill:true },
  ]}, options:{legend:{display:false}, scales:{xAxes:[{gridLines:{display:false},ticks:{fontColor:'#444',fontSize:13}}],yAxes:[axeY({ zero: true })]}} }
}

// ── Fonction principale ───────────────────────────────────────────────────
/** Génère et télécharge le rapport. Erreur si le serveur d'impression ne répond pas : aucun rapport de secours (19/10/2026). */
export async function generatePdf(d: PdfReportData): Promise<void> {
  var pdf = await construirePdf(d)
  var blob = new Blob([pdf], { type: 'application/pdf' })
  var url = URL.createObjectURL(blob)
  var a = document.createElement('a')
  a.href = url
  a.download = (d.projectName || 'Rapport').replace(/[^a-z0-9]/gi, '_') + '_rapport.pdf'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(function() { URL.revokeObjectURL(url) }, 5000)
}

/**
 * Rapport écrit en HTML (services/rapportHtml.ts), imprimé par le serveur (Gotenberg). Plus de rapport de secours sur
 * l'ancien gabarit (19/10/2026 : il n'était plus à jour) : si l'impression échoue, l'erreur remonte et l'écran l'affiche.
 */
export async function construirePdf(d: PdfReportData): Promise<Uint8Array> {
  var html = await construireHtml(d)
  var octets = new Uint8Array(await impressionHtml.imprimer(html))
  // contrôle : un vrai PDF, avec les 14 pages du rapport
  var lu = await PDFDocument.load(octets)
  if (lu.getPageCount() < 14) throw new Error('Rapport incomplet reçu du service d\'impression (' + lu.getPageCount() + ' pages)')
  return octets
}

// ── Rapport HTML : dessins, polices, graphiques ───────────────────────────
/** Dessins extraits du gabarit (outils/rapport-gabarit/extraire.sh), servis par le frontend sous /rapport/. */
var DESSINS: { [cle: string]: string } = {
  logoEpg: 'logo-epg.png', logosMarques: 'logos-marques.png', couverture: 'couverture.jpg', puce: 'puce.png',
  fondPresentation: 'fond-presentation.svg', fondProjet: 'fond-projet.svg', fondAgences: 'fond-agences.svg', synoptique: 'synoptique.jpg',
}
var TYPES: { [ext: string]: string } = { png: 'image/png', jpg: 'image/jpeg', svg: 'image/svg+xml' }
var GRAISSES: { [cle: string]: number } = { light: 300, regular: 400, medium: 500, semibold: 600, bold: 700, extrabold: 800 }
var FICHIERS_POLICES: { [cle: string]: string } = {
  light: 'Light', regular: 'Regular', medium: 'Medium', semibold: 'SemiBold', bold: 'Bold', extrabold: 'ExtraBold',
}

async function lireFichier(url: string): Promise<Uint8Array | null> {
  try {
    var r = await fetch(url)
    if (!r.ok) return null
    return new Uint8Array(await r.arrayBuffer())
  } catch (e) {
    return null
  }
}

/** Polices Poppins (base64) et mesure exacte des textes (pdf-lib) pour réduire ceux qui ne tiennent pas dans leur case. */
async function policesEtMesure(): Promise<{ polices: PolicesRapport; mesure: Mesure }> {
  var doc = await PDFDocument.create()
  doc.registerFontkit(fontkit)
  var polices: any = {}
  var parGraisse: { [g: number]: any } = {}
  var cles = Object.keys(FICHIERS_POLICES)
  var octets = await Promise.all(cles.map(function (c) { return lireFichier('/fonts/Poppins-' + FICHIERS_POLICES[c] + '.ttf') }))
  for (var i = 0; i < cles.length; i++) {
    if (!octets[i]) continue
    polices[cles[i]] = base64(octets[i] as Uint8Array)
    parGraisse[GRAISSES[cles[i]]] = await doc.embedFont(octets[i] as Uint8Array, { subset: true })
  }
  if (!polices.regular || !polices.medium || !polices.semibold) throw new Error('Polices Poppins introuvables')
  var mesure: Mesure = function (texte, graisse, taille) {
    var police = parGraisse[graisse] || parGraisse[graisse >= 600 ? 600 : 400]
    try { return police.widthOfTextAtSize(texte, taille) } catch (e) { return texte.length * taille * 0.6 }
  }
  return { polices: polices as PolicesRapport, mesure: mesure }
}

/** Graphiques du rapport (Chart.js et canvas, hors écran) en PNG, aux proportions des emplacements du rapport. */
export function graphiquesRapport(d: PdfReportData): ImagesRapport {
  var avertissement = d.avertissementsExtrapolation && d.avertissementsExtrapolation.length
  // journées types des saisons (03/11/2026) : jours réels les plus proches du jour moyen de la saison, en semaine et le
  // week-end ; calcul plus ancien : premiers jours de deux mois de la saison
  var SAISONS = [[2, 3], [5, 6], [8, 9], [11, 10]]
  var CLES = ['printemps', 'ete', 'automne', 'hiver']
  var donnees: any[] = []
  SAISONS.forEach(function (s, i) {
    [false, true].forEach(function (we, k) {
      var j = journeeType(d, CLES[i], we, 'conso+prod')
      donnees.push(j ? { firstDayHourlyProduction: j.production, firstDayHourlyConsumption: j.consommation,
        firstDayHourlyPurchase: j.achat, firstDayHourlyBatteryStock: j.stock } : d.monthlyData[s[k]])
    })
  })
  var page10 = dimensionsPage10(d)
  var jours: string[] = []
  var maxKw = maxJoursTypes(d, donnees)
  var hJour = Math.round(HAUTEUR_JOUR_TYPE * 2)
  donnees.forEach(function (m) { jours.push(renderChart(cfgJourType(d, m, maxKw), 528, hJour)) })
  return {
    jourConso: renderChart(cfgJourTypeSaisonnier(d), 1076, avertissement ? 316 : 402),
    prodConso: renderChart(cfgProdConso(d), 1076, 402),
    donutProd: renderChart(cfgDonutProd(d), 220, 220),
    donutConso: renderChart(cfgDonutConso(d), 220, 220),
    repartition: renderChart(cfgRepartition(d), 1076, 402),
    flux: renderSankey(d, 1078, Math.round(HAUTEUR_FLUX * 2)),
    joursTypes: jours,
    projection: renderChart(cfgProjection(d), 1078, Math.round(page10.projection * 2)),
    donutEconomies: renderChart(cfgDonutEco(d), Math.round(page10.donut * 2.8), Math.round(page10.donut * 2.8)),
    factures: renderChart(cfgFactures(d), 1076, 402),
    reseau: renderChart(cfgReseau(d), 1076, 402),
  }
}

/** Page HTML complète du rapport (polices, dessins et graphiques inclus : le serveur d'impression n'a pas de réseau). */
export async function construireHtml(d: PdfReportData): Promise<string> {
  var pm = await policesEtMesure()
  var images: any = graphiquesRapport(d)
  var cles = Object.keys(DESSINS)
  var fichiers = await Promise.all(cles.map(function (c) { return lireFichier('/rapport/' + DESSINS[c]) }))
  cles.forEach(function (c, i) {
    var f = fichiers[i]
    if (!f) { console.warn('Dessin du rapport introuvable :', DESSINS[c]); return }
    images[c] = 'data:' + TYPES[DESSINS[c].split('.').pop() as string] + ';base64,' + base64(f)
  })
  return documentRapport(d, images as ImagesRapport, pm.polices, pm.mesure)
}

// ── Impression par le serveur (Gotenberg) ────────────────────────────────
/** Impression d'une page HTML en PDF par le serveur ; remplaçable dans les tests. */
export var impressionHtml = {
  imprimer: async function (html: string): Promise<ArrayBuffer> {
    // chargés à l'appel : le service reste utilisable sans session (tests)
    var { axiosRequest } = await import('@/middlewares/axios')
    var { conf } = await import('@/config/config')
    return await axiosRequest.postRequest(conf.API_URL + '/wizard/rapport-pdf', html,
      { headers: { 'Content-Type': 'text/html; charset=utf-8' }, responseType: 'arraybuffer', timeout: 120000 })
  },
}

function base64(octets: Uint8Array): string {
  var morceaux: string[] = []
  for (var i = 0; i < octets.length; i += 0x8000) {
    morceaux.push(String.fromCharCode.apply(null, Array.prototype.slice.call(octets, i, i + 0x8000)))
  }
  return btoa(morceaux.join(''))
}


export function libelleSurplus(d: any): string {
  return libelleSurplusRapport(d)
}
